import React from 'react';
import { Card } from '../ui/Card';
import { Progress } from '../ui/Progress';
import { Badge } from '../ui/Badge';
import { TrendingUp, Zap, Database, Users, Folder, AlertTriangle, CheckCircle } from 'lucide-react';

interface UsageMetricsProps {
  usage: {
    apiCalls: { used: number; limit: number };
    aiTokens: { used: number; limit: number };
    projects: { used: number; limit: number };
    seats: { used: number; limit: number };
  };
  detailed?: boolean;
}

export function UsageMetrics({ usage, detailed = false }: UsageMetricsProps) {
  const getUsagePercentage = (used: number, limit: number) => {
    return Math.round((used / limit) * 100);
  };

  const getUsageStatus = (percentage: number) => {
    if (percentage >= 90) return { color: 'destructive' as const, icon: AlertTriangle, text: 'Critical' };
    if (percentage >= 75) return { color: 'secondary' as const, icon: AlertTriangle, text: 'High' };
    if (percentage >= 50) return { color: 'secondary' as const, icon: TrendingUp, text: 'Moderate' };
    return { color: 'default' as const, icon: CheckCircle, text: 'Good' };
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-600';
    if (percentage >= 75) return 'bg-orange-600';
    if (percentage >= 50) return 'bg-yellow-600';
    return 'bg-green-600';
  };

  const usageItems = [
    {
      title: 'API Calls',
      icon: Zap,
      used: usage.apiCalls.used,
      limit: usage.apiCalls.limit,
      unit: 'calls',
      description: 'Monthly API requests to our services'
    },
    {
      title: 'AI Tokens',
      icon: Database,
      used: usage.aiTokens.used,
      limit: usage.aiTokens.limit,
      unit: 'tokens',
      description: 'AI tokens used for analytics and insights'
    },
    {
      title: 'Projects',
      icon: Folder,
      used: usage.projects.used,
      limit: usage.projects.limit,
      unit: 'projects',
      description: 'Active projects in your account'
    },
    {
      title: 'Team Seats',
      icon: Users,
      used: usage.seats.used,
      limit: usage.seats.limit,
      unit: 'seats',
      description: 'Team members with access'
    }
  ];

  if (!detailed) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mr-3">
              <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Usage Overview</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">Current month usage</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {usageItems.map((item, index) => {
            const percentage = getUsagePercentage(item.used, item.limit);
            const status = getUsageStatus(percentage);
            
            return (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <item.icon className="w-4 h-4 text-gray-600 dark:text-gray-400 mr-2" />
                    <span className="font-medium text-gray-900 dark:text-white">{item.title}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {item.used.toLocaleString()} / {item.limit.toLocaleString()}
                    </span>
                    <Badge variant={status.color} className="text-xs">
                      {percentage}%
                    </Badge>
                  </div>
                </div>
                <Progress value={percentage} className="h-2" />
              </div>
            );
          })}
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {usageItems.map((item, index) => {
          const percentage = getUsagePercentage(item.used, item.limit);
          const status = getUsageStatus(percentage);
          
          return (
            <Card key={index} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mr-3">
                    <item.icon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{item.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{item.description}</p>
                  </div>
                </div>
                <Badge variant={status.color}>
                  <status.icon className="w-3 h-3 mr-1" />
                  {status.text}
                </Badge>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">
                    {item.used.toLocaleString()}
                  </span>
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    of {item.limit.toLocaleString()} {item.unit}
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-300">Usage</span>
                    <span className="font-medium text-gray-900 dark:text-white">{percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all duration-300 ${getProgressColor(percentage)}`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>

                <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-300">Remaining</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {(item.limit - item.used).toLocaleString()} {item.unit}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Usage Trends */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Usage Trends</h3>
        <div className="h-64 bg-gray-50 dark:bg-gray-800 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <TrendingUp className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
            <p className="text-gray-500 dark:text-gray-400">Usage trend chart would go here</p>
            <p className="text-sm text-gray-400 dark:text-gray-500">Track your usage patterns over time</p>
          </div>
        </div>
      </Card>

      {/* Usage Recommendations */}
      <Card className="p-6 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start">
          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-3 mt-1">
            <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">Usage Recommendations</h4>
            <div className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
              <p>• You're using 62% of your API calls. Consider optimizing your requests or upgrading your plan.</p>
              <p>• AI token usage is within normal range. Great job optimizing your prompts!</p>
              <p>• You have room for 1 more project and 3 more team members.</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
} 